import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';

import { createWarehouse } from '../../../api/gse/fn/warehouses/create-warehouse';
import { getAllWarehouses, GetAllWarehouses$Params } from '../../../api/gse/fn/warehouses/get-all-warehouses';
import { getWarehouse } from '../../../api/gse/fn/warehouses/get-warehouse';
import { updateWarehouse } from '../../../api/gse/fn/warehouses/update-warehouse';
import { createZone } from '../../../api/gse/fn/zones/create-zone';
import { updateZone } from '../../../api/gse/fn/zones/update-zone';
import { deleteZone } from '../../../api/gse/fn/zones/delete-zone';
import { CreateWarehouseRequest } from '../../../api/gse/models/create-warehouse-request';
import { UpdateWarehouseRequest } from '../../../api/gse/models/update-warehouse-request';
import { WarehouseZoneRequest } from '../../../api/gse/models/warehouse-zone-request';
import { WarehouseResponse } from '../../../api/gse/models/warehouse-response';
import { WarehouseZoneResponse } from '../../../api/gse/models/warehouse-zone-response';
import { GetWarehousesResponse } from '../../../api/gse/models/get-warehouses-response';
import { StrictHttpResponse } from '../../../api/gse/strict-http-response';
import { GseApiConfiguration } from '../../../api/gse/gse-api-configuration';
import { WarehouseLookupResponse } from '../../../api/gse/models/warehouse-lookup-response';
import { getZone } from '../../../api/gse/fn/zones/get-zone';
import { getZonesByWarehouse } from '../../../api/gse/fn/zones/get-zones-by-warehouse';
import { getWarehouseDashboardSummary } from '../../../api/gse/fn/warehouse-dashboard/get-warehouse-dashboard-summary';
import { getZoneTypeDistribution } from '../../../api/gse/fn/warehouse-dashboard/get-zone-type-distribution';
import { getWarehousesByZoneCount } from '../../../api/gse/fn/warehouse-dashboard/get-warehouses-by-zone-count';
import { WarehouseDashboardSummaryResponse } from '../../../api/gse/models/warehouse-dashboard-summary-response';
import { ZoneTypeDistributionResponse } from '../../../api/gse/models/zone-type-distribution-response';
import { WarehousesByZoneCountResponse } from '../../../api/gse/models/warehouses-by-zone-count-response';
import { WarehouseZoneListResponse } from '../../../api/gse/models/warehouse-zone-list-response';
import { getWarehouseLookups } from '../../../api/gse/functions';

@Injectable({
  providedIn: 'root',
})
export class WarehouseService {
  private http = inject(HttpClient);
  private config = inject(GseApiConfiguration);

  /**
   * Create a new warehouse
   * @param request The warehouse creation request data
   * @returns Observable with the warehouse response
   */
  createWarehouse(request: CreateWarehouseRequest): Observable<StrictHttpResponse<WarehouseResponse>> {
    return createWarehouse(this.http, this.config.rootUrl, { body: request });
  }

  /**
   * Get warehouses (optional `status` filters by {@link GetAllWarehouses$Params.status}).
   */
  getAllWarehouses(params?: GetAllWarehouses$Params): Observable<StrictHttpResponse<GetWarehousesResponse>> {
    return getAllWarehouses(this.http, this.config.rootUrl, params);
  }

  /**
   * Get a specific warehouse by ID
   * @param id The warehouse ID
   * @returns Observable with the warehouse response
   */
  getWarehouse(id: number): Observable<StrictHttpResponse<WarehouseResponse>> {
    return getWarehouse(this.http, this.config.rootUrl, { id });
  }

  /**
   * Update an existing warehouse
   * @param id The warehouse ID to update
   * @param request The warehouse update request data
   * @returns Observable with the updated warehouse response
   */
  updateWarehouse(id: number, request: UpdateWarehouseRequest): Observable<StrictHttpResponse<WarehouseResponse>> {
    return updateWarehouse(this.http, this.config.rootUrl, { id, body: request });
  }

  /**
   * Create a new zone in a warehouse
   * @param warehouseId The warehouse ID
   * @param request The zone creation request data
   * @returns Observable with the zone response
   */
  createZone(warehouseId: number, request: WarehouseZoneRequest): Observable<StrictHttpResponse<WarehouseZoneResponse>> {
    return createZone(this.http, this.config.rootUrl, { warehouseId, body: request });
  }

  /**
   * Update an existing zone in a warehouse
   * @param warehouseId The warehouse ID
   * @param zoneId The zone ID
   * @param request The zone update request data
   * @returns Observable with the zone response
   */
  updateZone(warehouseId: number, zoneId: number, request: WarehouseZoneRequest): Observable<StrictHttpResponse<WarehouseZoneResponse>> {
    return updateZone(this.http, this.config.rootUrl, { warehouseId, zoneId, body: request });
  }

  /**
   * Delete a zone from a warehouse
   * @param warehouseId The warehouse ID
   * @param zoneId The zone ID
   * @returns Observable with an empty response body
   */
  deleteZone(warehouseId: number, zoneId: number): Observable<StrictHttpResponse<void>> {
    return deleteZone(this.http, this.config.rootUrl, { warehouseId, zoneId });
  }

  /**
   * Get warehouse lookups (zone types, space types)
   * @returns Observable with the lookup response
   */
  getWarehouseLookups(): Observable<StrictHttpResponse<WarehouseLookupResponse>> {
    return getWarehouseLookups(this.http, this.config.rootUrl);
  }

  /**
   * Get warehouse dashboard summary
   * @returns Observable with the summary response
   */
  getWarehouseDashboardSummary(): Observable<StrictHttpResponse<WarehouseDashboardSummaryResponse>> {
    return getWarehouseDashboardSummary(this.http, this.config.rootUrl);
  }

  /**
   * Get zone type distribution
   * @returns Observable with the distribution response
   */
  getZoneTypeDistribution(): Observable<StrictHttpResponse<ZoneTypeDistributionResponse>> {
    return getZoneTypeDistribution(this.http, this.config.rootUrl);
  }

  /**
   * Get warehouses by zone count
   * @returns Observable with the count response
   */
  getWarehousesByZoneCount(): Observable<StrictHttpResponse<WarehousesByZoneCountResponse>> {
    return getWarehousesByZoneCount(this.http, this.config.rootUrl);
  }

  /**
   * Get all zones for a warehouse
   * @param warehouseId The warehouse ID
   * @returns Observable with the list of zones
   */
  getZonesByWarehouse(warehouseId: number): Observable<StrictHttpResponse<WarehouseZoneListResponse>> {
    return getZonesByWarehouse(this.http, this.config.rootUrl, { warehouseId });
  }

  /**
   * Get zone details
   * @param warehouseId The warehouse ID
   * @param zoneId The zone ID
   * @returns Observable with the zone response
   */
  getZone(warehouseId: number, zoneId: number): Observable<StrictHttpResponse<WarehouseZoneResponse>> {
    return getZone(this.http, this.config.rootUrl, { warehouseId, zoneId });
  }

}
